import { deriveLogicalProjectKey } from "./logicalProject";
import type { Project } from "./types";

/** A project as far as group addressing is concerned. */
type AddressableProject = Pick<Project, "environmentId" | "id" | "repositoryIdentity">;

export interface ProjectGroupRoute {
  readonly groupKey: string;
  /** True when the key the caller supplied is already the canonical one. */
  readonly canonical: boolean;
}

/**
 * Which group does this key address, and is the key already canonical?
 *
 * Accepts more than it emits, on purpose. Every link written before this
 * existed — including ones already sitting in CMMD task cards — carries a
 * project id rather than a group key, so a project's own id resolves into its
 * group and the caller rewrites the URL. The URL is a hint; the group is the
 * thing. That tolerance is also the migration path if the canonical key
 * derivation ever changes.
 */
export function resolveProjectGroupRoute(
  key: string,
  projects: readonly AddressableProject[],
): ProjectGroupRoute | null {
  let byMemberId: string | null = null;
  for (const project of projects) {
    const groupKey = deriveLogicalProjectKey(project);
    if (groupKey === key) return { groupKey, canonical: true };
    // Weaker match, so it never wins over an exact group key: keep looking.
    if (byMemberId === null && project.id === key) byMemberId = groupKey;
  }
  return byMemberId === null ? null : { groupKey: byMemberId, canonical: false };
}

/** A CMMD board as far as repo matching is concerned. */
interface AddressableBoard {
  readonly id: string;
  readonly githubRepoUrl?: string | null;
}

/**
 * Every CMMD board linked to a group's repository.
 *
 * A LIST, not one board: CMMD's schema is explicitly many-to-many — "a project
 * links many repos and a repo may be linked from many projects" — so a repo
 * having several boards is the designed case, not an edge one.
 *
 * Matched on canonical repo identity rather than on any Forge-side id, which is
 * what lets one link serve every checkout of that repo on every machine.
 */
export function resolveBoardsForGroup<T extends AddressableBoard>(
  groupKey: string,
  boards: readonly T[],
): readonly T[] {
  const target = canonicalRepoKeyOf(groupKey);
  if (target === null) return [];
  return boards.filter((board) => canonicalRepoKeyOf(board.githubRepoUrl) === target);
}

/**
 * Reduces either side of the comparison to `host/owner/name`.
 *
 * Both sides arrive in different shapes — a group key is already normalized
 * (`github.com/acme/app`, possibly with a `#subpath`), while a board carries
 * whatever URL a human or an API stored. Comparing them raw is how a trailing
 * `.git`, a `git@` remote, or a case difference silently loses a link.
 */
export function canonicalRepoKeyOf(value: string | null | undefined): string | null {
  const raw = value?.trim();
  if (!raw) return null;
  const withoutSubpath = raw.split("#")[0] ?? "";
  const scp = /^git@([^:]+):(.+)$/i.exec(withoutSubpath);
  const path = scp
    ? `${scp[1]}/${scp[2]}`
    : withoutSubpath.replace(/^[a-z0-9+.-]+:\/\//i, "").replace(/^[^@]+@/, "");
  const cleaned = path.replace(/\.git$/i, "").toLowerCase();
  // Must actually LOOK like a repository — `host/owner/name`. Without this the
  // function happily returns a "repo key" for anything, including the
  // `envId\0projectId` fallback a repo-less project is keyed by, which would
  // let a scratch folder match a board that stored the same string.
  const segments = cleaned.split("/").filter((segment) => segment.length > 0);
  if (segments.length < 3 || segments.some(hasControlCharacter)) {
    return null;
  }
  return segments.join("/");
}

/** True when any character is a C0 control — checked by code point so the source stays printable. */
function hasControlCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code <= 0x1f) return true;
  }
  return false;
}
